import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, FolderOpen, LayoutGrid, Plus, Search, User } from 'lucide-react';
import { Button } from './ui/Button';

export type TaskControlScope = 'ACTIVE' | 'COMPLETED' | 'ALL';
export type TaskControlDueFilter = 'ALL' | 'OVERDUE' | 'TODAY' | 'NEXT_7_DAYS' | 'NO_DATE';

export const COMPLETED_TASK_PAGE_SIZE = 25;

export const isCancelledTask = (task: {status?: string | null}) =>
  ['CANCELLED', 'CANCELED'].includes(String(task.status || '').toUpperCase());

type TaskControlTask = {
  id: string;
  title?: string | null;
  description?: string | null;
  status?: string | null;
  due_date?: string | null;
  assignee_id?: string | null;
  assignee?: { full_name?: string | null } | null;
  lead?: { project_ref?: string | null; first_name?: string | null; last_name?: string | null } | null;
  project?: { title?: string | null } | null;
};

export const selectTaskControlTasks = (
  tasks: TaskControlTask[],
  scope: TaskControlScope,
  search: string,
  completedLimit: number,
  dueFilter: TaskControlDueFilter = 'ALL',
  today = new Date()
) => {
  const query = search.trim().toLowerCase();
  const searchedTasks = query
    ? tasks.filter((task) => [
        task.title,
        task.description,
        task.assignee?.full_name,
        task.lead?.project_ref,
        task.lead?.first_name,
        task.lead?.last_name,
        task.project?.title,
      ].some((value) => String(value || '').toLowerCase().includes(query)))
    : tasks;

  const dayStart = new Date(today);
  dayStart.setHours(0, 0, 0, 0);
  const tomorrow = new Date(dayStart);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek = new Date(dayStart);
  nextWeek.setDate(nextWeek.getDate() + 8);

  const matchingTasks = searchedTasks.filter((task) => {
    if (dueFilter === 'ALL') return true;
    if (!task.due_date) return dueFilter === 'NO_DATE';
    const dueDate = new Date(task.due_date);
    if (Number.isNaN(dueDate.getTime())) return dueFilter === 'NO_DATE';
    if (dueFilter === 'OVERDUE') return dueDate < dayStart;
    if (dueFilter === 'TODAY') return dueDate >= dayStart && dueDate < tomorrow;
    if (dueFilter === 'NEXT_7_DAYS') return dueDate >= dayStart && dueDate < nextWeek;
    return false;
  });

  const activeTasks = matchingTasks.filter((task) => task.status !== 'COMPLETED' && !isCancelledTask(task));
  const cancelledTasks = matchingTasks.filter(isCancelledTask);
  const completedTasks = matchingTasks.filter((task) => task.status === 'COMPLETED');
  const visibleCompletedTasks = completedTasks.slice(0, completedLimit);

  return {
    activeTasks,
    completedTasks,
    cancelledTasks,
    visibleTasks: scope === 'ACTIVE'
      ? activeTasks
      : scope === 'COMPLETED'
        ? visibleCompletedTasks
        : [...activeTasks, ...cancelledTasks, ...visibleCompletedTasks],
    hasMoreCompleted: scope !== 'ACTIVE' && visibleCompletedTasks.length < completedTasks.length,
  };
};

type TaskControlWorkspaceProps = {
  tasks: TaskControlTask[];
  staff: Array<{ id: string; full_name: string }>;
  isGrouped: boolean;
  taskFilter: 'ALL' | 'MINE';
  assigneeFilter: string;
  projectName?: string;
  onGroupedChange: (grouped: boolean) => void;
  onTaskFilterChange: (filter: 'ALL' | 'MINE') => void;
  onAssigneeFilterChange: (staffId: string) => void;
  onClearProjectFilter: () => void;
  onCreateTask: () => void;
  children: (tasks: TaskControlTask[], scope: TaskControlScope) => React.ReactNode;
};

export const TaskControlWorkspace = ({
  tasks,
  staff,
  isGrouped,
  taskFilter,
  assigneeFilter,
  projectName,
  onGroupedChange,
  onTaskFilterChange,
  onAssigneeFilterChange,
  onClearProjectFilter,
  onCreateTask,
  children,
}: TaskControlWorkspaceProps) => {
  const [scope, setScope] = useState<TaskControlScope>('ACTIVE');
  const [search, setSearch] = useState('');
  const [dueFilter, setDueFilter] = useState<TaskControlDueFilter>('ALL');
  const [completedLimit, setCompletedLimit] = useState(COMPLETED_TASK_PAGE_SIZE);

  useEffect(() => {
    setCompletedLimit(COMPLETED_TASK_PAGE_SIZE);
  }, [scope, search, dueFilter, assigneeFilter, taskFilter, projectName]);

  const selection = useMemo(
    () => selectTaskControlTasks(tasks, scope, search, completedLimit, dueFilter),
    [tasks, scope, search, completedLimit, dueFilter]
  );

  const changeScope = (nextScope: TaskControlScope) => {
    setScope(nextScope);
  };

  return (
    <section className="animate-fade-in-up mt-2 min-w-0" aria-label="Task Control workspace">
      <div className="mb-5 flex min-w-0 flex-col gap-5 md:mb-8">
        <div className="flex flex-col items-start justify-between gap-4 xl:flex-row xl:items-end">
          <div className="min-w-0">
            <h1 className="type-page-title break-words text-white">
              {projectName ? `Project: ${projectName}` : 'Task Control'}
            </h1>
            <p className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 md:mt-4 md:tracking-[0.25em]">
              <CheckCircle size={13} className="shrink-0 text-brand-neonPink" />
              {projectName ? 'Isolated Operations Context' : 'Global Operations Pipeline'}
            </p>
          </div>

          <Button
            onClick={onCreateTask}
            className="min-h-11 w-full whitespace-nowrap rounded-xl border-none bg-brand-neonPink px-6 py-3 text-sm font-bold text-white shadow-neon-pink sm:w-auto"
          >
            <Plus size={18} className="mr-2" /> Initialize Task
          </Button>
        </div>

        <div className="grid min-h-11 grid-cols-3 rounded-2xl border border-white/10 bg-slate-900/80 p-1" role="tablist" aria-label="Task status view">
          {([
            ['ACTIVE', `Active (${selection.activeTasks.length})`],
            ['COMPLETED', `Completed (${selection.completedTasks.length})`],
            ['ALL', 'All'],
          ] as Array<[TaskControlScope, string]>).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={scope === value}
              onClick={() => changeScope(value)}
              className={`min-h-11 min-w-0 rounded-xl px-2 text-[11px] font-bold transition-colors sm:text-xs ${
                scope === value ? 'bg-brand-neonBlue text-slate-950 shadow-neon' : 'text-slate-400 hover:text-white'
              }`}
            >
              <span className="block truncate">{label}</span>
            </button>
          ))}
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(240px,1fr)_auto_auto]">
          <label className="relative block min-w-0">
            <span className="sr-only">Search tasks</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search tasks, jobs or assignees"
              className="min-h-11 w-full min-w-0 rounded-xl border border-white/10 bg-slate-900/80 py-3 pl-11 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-brand-neonBlue"
            />
          </label>

          <div className="grid min-w-0 grid-cols-2 gap-2 sm:flex">
            <button
              type="button"
              onClick={() => {
                onTaskFilterChange(taskFilter === 'MINE' ? 'ALL' : 'MINE');
                onAssigneeFilterChange('ALL');
              }}
              className={`flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-bold ${
                taskFilter === 'MINE'
                  ? 'border-brand-neonPink bg-brand-neonPink text-white'
                  : 'border-white/10 bg-slate-900/80 text-slate-300'
              }`}
            >
              <User size={15} /> My Tasks
            </button>
            <label className="min-w-0">
              <span className="sr-only">Filter by assignee</span>
              <select
                value={assigneeFilter}
                onChange={(event) => {
                  onAssigneeFilterChange(event.target.value);
                  if (event.target.value !== 'ALL') onTaskFilterChange('ALL');
                }}
                className="min-h-11 w-full min-w-0 rounded-xl border border-white/10 bg-slate-900/80 px-3 text-xs font-bold text-slate-300 outline-none focus:border-brand-neonBlue sm:w-40"
              >
                <option value="ALL">All assignees</option>
                <option value="UNASSIGNED">Unassigned</option>
                {staff.map((member) => (
                  <option key={member.id} value={member.id}>{member.full_name}</option>
                ))}
              </select>
            </label>
            <label className="col-span-2 min-w-0 sm:col-span-1">
              <span className="sr-only">Filter by due date</span>
              <select
                value={dueFilter}
                onChange={(event) => setDueFilter(event.target.value as TaskControlDueFilter)}
                className="min-h-11 w-full min-w-0 rounded-xl border border-white/10 bg-slate-900/80 px-3 text-xs font-bold text-slate-300 outline-none focus:border-brand-neonBlue sm:w-36"
              >
                <option value="ALL">All dates</option>
                <option value="OVERDUE">Overdue</option>
                <option value="TODAY">Due today</option>
                <option value="NEXT_7_DAYS">Next 7 days</option>
                <option value="NO_DATE">No due date</option>
              </select>
            </label>
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-2 sm:flex">
            <div className="grid min-h-11 grid-cols-2 rounded-xl border border-white/10 bg-slate-900/80 p-1">
              <button
                type="button"
                onClick={() => onGroupedChange(false)}
                className={`flex min-h-11 items-center justify-center gap-1 rounded-lg px-3 text-xs font-bold ${!isGrouped ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              >
                <LayoutGrid size={14} /> Flat
              </button>
              <button
                type="button"
                onClick={() => onGroupedChange(true)}
                className={`flex min-h-11 items-center justify-center gap-1 rounded-lg px-3 text-xs font-bold ${isGrouped ? 'bg-brand-neonBlue text-slate-950' : 'text-slate-400'}`}
              >
                <FolderOpen size={14} /> By Job
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                onTaskFilterChange('ALL');
                onAssigneeFilterChange('ALL');
                onClearProjectFilter();
                setSearch('');
                setDueFilter('ALL');
              }}
              className="min-h-11 rounded-xl border border-white/10 bg-slate-900/80 px-3 text-xs font-bold text-slate-300 hover:text-white"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        Showing {selection.visibleTasks.length} of {selection.activeTasks.length + selection.completedTasks.length + selection.cancelledTasks.length} matching tasks
      </div>

      {children(selection.visibleTasks, scope)}

      {selection.hasMoreCompleted && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setCompletedLimit((current) => current + COMPLETED_TASK_PAGE_SIZE)}
            className="min-h-11 rounded-xl border border-brand-neonBlue/40 bg-brand-neonBlue/10 px-6 text-sm font-bold text-brand-neonBlue hover:bg-brand-neonBlue/20"
          >
            Load 25 more completed tasks
          </button>
        </div>
      )}
    </section>
  );
};
